/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AttacksPerMonth,
  DistinctThreatActorsInSource,
  DistinctThreatActorsTargetingCountryList,
  SourceToTargetPewPew,
  ThreatActorByMonth,
  ThreatsByDevice,
  TopSourceCountry,
  TopTargetCountry,
  TopThreatActorsLast12Months,
  TotalAttackBySourceCountry,
  TotalAttackByTargetCountry,
} from '../types/StatTypes'
import { countryCoordinates } from './location-mapper'

export const mapIntelToThreatsByDevice = (threatsByDevice: ThreatsByDevice[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.device_platform !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.device_platform, threatByDevice.total_threat_events])
}

export const mapIntelToTopSourceCountries = (threatsByDevice: TopSourceCountry[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.source_country !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.source_country, threatByDevice.total_threat_events])
}

export const mapIntelToTopTargetCountries = (threatsByDevice: TopTargetCountry[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.target_country !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.target_country, threatByDevice.total_threat_events])
}

export const mapIntelToTopThreatActorsIn12Months = (threatsByDevice: TopThreatActorsLast12Months[]) => {
  return threatsByDevice
    .filter((threatByDevice) => threatByDevice.threat_actor !== 'Unknown')
    .map((threatByDevice) => [threatByDevice.threat_actor, threatByDevice.total_threats])
}

export const mapIntelToAttacksPerMonthHeatmap = (attacksPerMonth: AttacksPerMonth[]) => {
  const years = [...new Set(attacksPerMonth.map((item) => item.yr))].sort()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const heatmapData = attacksPerMonth.map((item) => {
    const x = item.mo - 1
    const y = years.indexOf(item.yr)
    return [x, y, item.total_threats]
  })

  return { yAxis: years, xAxis: months, heatmapData }
}

export const mapIntelToAttackByTargetMap = (apiData: TotalAttackByTargetCountry[]) => {
  let csvString = 'Country,Code,Threat Events\n'

  apiData.forEach((item) => {
    const countryCode = countryNameToCode[item.target_country] || 'N/A'
    if (countryCode !== 'N/A') {
      csvString += `${item.target_country},${countryCode},${item.total_threat_events}\n`
    }
  })

  return csvString
}

export const mapIntelToAttackBySourceMap = (apiData: TotalAttackBySourceCountry[]) => {
  let csvString = 'Country,Code,Threat Events\n'

  apiData.forEach((item) => {
    const countryCode = countryNameToCode[item.source_country] || 'N/A'
    if (countryCode !== 'N/A') {
      csvString += `${item.source_country},${countryCode},${item.total_threat_events}\n`
    }
  })

  return csvString
}

export const mapIntelToThreatsByActorTarget = (
  apiData: { target_country: string; threat_actor: string; total_threats: number }[]
): { targetCategories: string[]; targetSeries: any[] } => {
  const uniqueCountries = [...new Set(apiData.map((entry) => entry.target_country))]

  const threatActors = [
    ...new Set(
      apiData.filter((entry) => uniqueCountries.includes(entry.target_country)).map((entry) => entry.threat_actor)
    ),
  ]

  const series: any = threatActors
    .map((actor) => {
      const data = uniqueCountries
        .map((country) => {
          const threat = apiData.find((entry) => entry.target_country === country && entry.threat_actor === actor)
          return threat ? threat.total_threats : null
        })
        .filter((value) => value !== null)

      return data.length > 0 ? { name: actor, type: 'column', data } : null
    })
    .filter((series) => series !== null) as any

  return { targetCategories: uniqueCountries, targetSeries: series }
}

export const mapIntelToThreatsByActorSource = (
  apiData: { source_country: string; threat_actor: string; total_threats: number }[]
): { categories: string[]; series: any[] } => {
  const filteredData = apiData.filter((entry) => entry.total_threats > 0)
  const uniqueCountries = [...new Set(filteredData.map((entry) => entry.source_country))]
  const threatActors = [...new Set(filteredData.map((entry) => entry.threat_actor))]

  const series = threatActors
    .map((actor) => {
      const data = uniqueCountries.map((country) => {
        const threat = filteredData.find((entry) => entry.source_country === country && entry.threat_actor === actor)
        return threat ? threat.total_threats : null
      })
      return data.some((value) => value !== null)
        ? { name: actor, type: 'column', stack: 'threats', data, minPointLength: 5 }
        : null
    })
    .filter((series) => series !== null)

  return { categories: uniqueCountries, series }
}

export const mapIntelToAptTargetMap = (apiData: DistinctThreatActorsTargetingCountryList[]) => {
  let csvString = 'Country,Code,Threat Events\n'

  apiData.forEach((item) => {
    const countryCode = countryNameToCode[item.target_country] || 'N/A'
    if (countryCode !== 'N/A') {
      csvString += `${item.target_country},${countryCode},${item.threat_actor_count}\n`
    }
  })

  return csvString
}

export const mapIntelToAptSourceMap = (apiData: DistinctThreatActorsInSource[]) => {
  let csvString = 'Country,Code,Threat Events\n'

  apiData.forEach((item) => {
    const countryCode = countryNameToCode[item.source_country] || 'N/A'
    if (countryCode !== 'N/A') {
      csvString += `${item.source_country},${countryCode},${item.threat_actor_count}\n`
    }
  })

  return csvString
}

export const mapIntelToThreatsActorEventsIn12Months = (attacksPerMonth: ThreatActorByMonth[]) => {
  const filteredData = attacksPerMonth.filter((entry) => entry.threat_actor !== 'Unknown')
  const months = [...new Set(filteredData.map((entry) => entry.month.slice(0, 7)))]
  const threatActors = [...new Set(filteredData.map((entry) => entry.threat_actor))]

  const heatmapData = filteredData
    .map((entry) => {
      const x = months.indexOf(entry.month.slice(0, 7))
      const y = threatActors.indexOf(entry.threat_actor)
      return x !== -1 && y !== -1 ? [x, y, entry.total_threats] : null
    })
    .filter((item) => item !== null)

  return { xAxis: months, yAxis: threatActors, heatmapData }
}

export const mapIntelToHeatmapThreatEventsByMonth = (
  apiData: { month: string; source_country: string; total_threat_events: number }[]
): { xAxis: string[]; yAxis: string[]; heatmapData: any[] } => {
  const months = [...new Set(apiData.map((entry) => entry.month.slice(0, 7)))].sort()
  const countries = [...new Set(apiData.map((entry) => entry.source_country))].sort()

  const heatmapData = apiData
    .filter((entry) => entry.total_threat_events > 0)
    .map((entry) => {
      const x = months.indexOf(entry.month.slice(0, 7))
      const y = countries.indexOf(entry.source_country)
      return [x, y, entry.total_threat_events]
    })

  return { xAxis: months, yAxis: countries, heatmapData }
}

export const mapIntelToHeatmapThreatTargetEventsByMonth = (
  apiData: { month: string; target_country: string; total_threat_events: number }[]
): { xAxis: string[]; yAxis: string[]; heatmapData: any[] } => {
  const months = [...new Set(apiData.map((entry) => entry.month.slice(0, 7)))].sort()
  const countries = [...new Set(apiData.map((entry) => entry.target_country))].sort()

  const heatmapData = apiData
    .filter((entry) => entry.total_threat_events > 0)
    .map((entry) => {
      const x = months.indexOf(entry.month.slice(0, 7))
      const y = countries.indexOf(entry.target_country)
      return [x, y, entry.total_threat_events]
    })

  return { xAxis: months, yAxis: countries, heatmapData }
}

export const pewPewMapLinesMapper = (attacks: SourceToTargetPewPew[], lineColor: string) => {
  if (attacks.length === 0) return []

  return attacks
    .map(({ source_country, target_country }) => {
      const sourceCoords = countryCoordinates[source_country]
      const targetCoords = countryCoordinates[target_country]

      if (!sourceCoords || !targetCoords) return null

      const path = interpolatePath(sourceCoords, targetCoords, 100)

      return {
        type: 'mapline',
        data: [
          {
            geometry: {
              type: 'LineString',
              coordinates: path,
            },
            color: lineColor,
          },
        ],
        lineWidth: 1,
      }
    })
    .filter(Boolean)
}

const interpolatePath = (start: number[], end: number[], steps = 2) => {
  const stepFactor = 1 / (steps - 1)
  return Array.from({ length: steps }, (_, i) => [
    start[0] + (end[0] - start[0]) * (i * stepFactor),
    start[1] + (end[1] - start[1]) * (i * stepFactor),
  ])
}

export const pewPewMapPointsMapper = (attacks: SourceToTargetPewPew[]) => {
  const uniqueCountries = new Set()

  return {
    type: 'mappoint',
    joinBy: null,
    marker: {
      symbol: 'circle',
      radius: 4,
    },
    data: attacks.flatMap(({ source_country, target_country }) => {
      const sourceCoords = countryCoordinates[source_country]
      const targetCoords = countryCoordinates[target_country]

      const points = []
      if (sourceCoords && !uniqueCountries.has(source_country)) {
        uniqueCountries.add(source_country)
        points.push({
          geometry: { type: 'Point', coordinates: sourceCoords },
          name: source_country,
        })
      }
      if (targetCoords && !uniqueCountries.has(target_country)) {
        uniqueCountries.add(target_country)
        points.push({
          geometry: { type: 'Point', coordinates: targetCoords },
          name: target_country,
        })
      }
      return points
    }),
    color: '#f03e3e',
  }
}

export const countryNameToCode: { [key: string]: string } = {
  Afghanistan: 'AFG',
  Albania: 'ALB',
  Algeria: 'DZA',
  Andorra: 'AND',
  Angola: 'AGO',
  'Antigua and Barbuda': 'ATG',
  Argentina: 'ARG',
  Armenia: 'ARM',
  Australia: 'AUS',
  Austria: 'AUT',
  Azerbaijan: 'AZE',
  Bahamas: 'BHS',
  Bahrain: 'BHR',
  Bangladesh: 'BGD',
  Barbados: 'BRB',
  Belarus: 'BLR',
  Belgium: 'BEL',
  Belize: 'BLZ',
  Benin: 'BEN',
  Bhutan: 'BTN',
  Bolivia: 'BOL',
  'Bosnia and Herzegovina': 'BIH',
  'Bosnia And Herzegovina': 'BIH',
  Botswana: 'BWA',
  Brazil: 'BRA',
  Brunei: 'BRN',
  Bulgaria: 'BGR',
  'Burkina Faso': 'BFA',
  Burundi: 'BDI',
  'Cabo Verde': 'CPV',
  Cambodia: 'KHM',
  Cameroon: 'CMR',
  Canada: 'CAN',
  'Central African Republic': 'CAF',
  Chad: 'TCD',
  Chile: 'CHL',
  China: 'CHN',
  Colombia: 'COL',
  Comoros: 'COM',
  'Congo (Congo-Brazzaville)': 'COG',
  'Congo (Congo-Kinshasa)': 'COD',
  'Costa Rica': 'CRI',
  Croatia: 'HRV',
  Cuba: 'CUB',
  Cyprus: 'CYP',
  'Czech Republic': 'CZE',
  Denmark: 'DNK',
  Djibouti: 'DJI',
  Dominica: 'DMA',
  'Dominican Republic': 'DOM',
  Ecuador: 'ECU',
  Egypt: 'EGY',
  'El Salvador': 'SLV',
  'Equatorial Guinea': 'GNQ',
  Eritrea: 'ERI',
  Estonia: 'EST',
  Eswatini: 'SWZ',
  Ethiopia: 'ETH',
  Fiji: 'FJI',
  Finland: 'FIN',
  France: 'FRA',
  Gabon: 'GAB',
  Gambia: 'GMB',
  Georgia: 'GEO',
  Germany: 'DEU',
  Ghana: 'GHA',
  Greece: 'GRC',
  Grenada: 'GRD',
  Guatemala: 'GTM',
  Guinea: 'GIN',
  'Guinea-Bissau': 'GNB',
  Guyana: 'GUY',
  Haiti: 'HTI',
  Honduras: 'HND',
  Hungary: 'HUN',
  Iceland: 'ISL',
  India: 'IND',
  Indonesia: 'IDN',
  Iran: 'IRN',
  Iraq: 'IRQ',
  Ireland: 'IRL',
  Israel: 'ISR',
  Italy: 'ITA',
  Jamaica: 'JAM',
  Japan: 'JPN',
  Jordan: 'JOR',
  Kazakhstan: 'KAZ',
  Kenya: 'KEN',
  Kiribati: 'KIR',
  'North Korea': 'PRK',
  'South Korea': 'KOR',
  Kuwait: 'KWT',
  Kyrgyzstan: 'KGZ',
  Laos: 'LAO',
  Latvia: 'LVA',
  Lebanon: 'LBN',
  Lesotho: 'LSO',
  Liberia: 'LBR',
  Libya: 'LBY',
  Liechtenstein: 'LIE',
  Lithuania: 'LTU',
  Luxembourg: 'LUX',
  Madagascar: 'MDG',
  Malawi: 'MWI',
  Malaysia: 'MYS',
  Maldives: 'MDV',
  Mali: 'MLI',
  Malta: 'MLT',
  'Marshall Islands': 'MHL',
  Mauritania: 'MRT',
  Mauritius: 'MUS',
  Mexico: 'MEX',
  Micronesia: 'FSM',
  Moldova: 'MDA',
  Monaco: 'MCO',
  Mongolia: 'MNG',
  Montenegro: 'MNE',
  Morocco: 'MAR',
  Mozambique: 'MOZ',
  Myanmar: 'MMR',
  Namibia: 'NAM',
  Nauru: 'NRU',
  Nepal: 'NPL',
  Netherlands: 'NLD',
  'New Zealand': 'NZL',
  Nicaragua: 'NIC',
  Niger: 'NER',
  Nigeria: 'NGA',
  'North Macedonia': 'MKD',
  Norway: 'NOR',
  Oman: 'OMN',
  Pakistan: 'PAK',
  Palau: 'PLW',
  Panama: 'PAN',
  'Papua New Guinea': 'PNG',
  Paraguay: 'PRY',
  Peru: 'PER',
  Philippines: 'PHL',
  Poland: 'POL',
  Portugal: 'PRT',
  Qatar: 'QAT',
  Romania: 'ROU',
  Russia: 'RUS',
  Rwanda: 'RWA',
  'Saint Kitts and Nevis': 'KNA',
  'Saint Lucia': 'LCA',
  'Saint Vincent and the Grenadines': 'VCT',
  Samoa: 'WSM',
  'San Marino': 'SMR',
  'Sao Tome and Principe': 'STP',
  'Saudi Arabia': 'SAU',
  Senegal: 'SEN',
  Serbia: 'SRB',
  Seychelles: 'SYC',
  'Sierra Leone': 'SLE',
  Singapore: 'SGP',
  Slovakia: 'SVK',
  Slovenia: 'SVN',
  'Solomon Islands': 'SLB',
  Somalia: 'SOM',
  'South Africa': 'ZAF',
  'South Sudan': 'SSD',
  Spain: 'ESP',
  'Sri Lanka': 'LKA',
  Sudan: 'SDN',
  Suriname: 'SUR',
  Sweden: 'SWE',
  Switzerland: 'CHE',
  Syria: 'SYR',
  Tajikistan: 'TJK',
  Tanzania: 'TZA',
  Thailand: 'THA',
  Togo: 'TGO',
  Tonga: 'TON',
  'Trinidad and Tobago': 'TTO',
  Tunisia: 'TUN',
  Turkey: 'TUR',
  Turkmenistan: 'TKM',
  Tuvalu: 'TUV',
  Uganda: 'UGA',
  Ukraine: 'UKR',
  'United Arab Emirates': 'ARE',
  'United Kingdom': 'GBR',
  'United States': 'USA',
  Uruguay: 'URY',
  Uzbekistan: 'UZB',
  Vanuatu: 'VUT',
  'Vatican City': 'VAT',
  Venezuela: 'VEN',
  Vietnam: 'VNM',
  Yemen: 'YEM',
  Zambia: 'ZMB',
  Zimbabwe: 'ZWE',
}
